import { useCallback, useContext, useState } from "react";
import { UnsavedChangesOnLogoutError, useAuth } from "@/context/auth-context";
import { FriendModeContext } from "@/context/friend-mode-context";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

type Problem = "unsaved" | "failed" | null;

/**
 * "Cerrar sesión" with feedback. Logout saves pending edits first; when that
 * fails (offline, server error) it asks before dropping them instead of
 * silently staying logged in. Render `logoutDialog` next to the trigger.
 */
export function useLogoutWithConfirm() {
  const { logout } = useAuth();
  const friendSession = useContext(FriendModeContext)?.session ?? null;
  const [problem, setProblem] = useState<Problem>(null);
  const [open, setOpen] = useState(false);

  const report = useCallback((error: unknown) => {
    console.error("Logout failed", error);
    setProblem(error instanceof UnsavedChangesOnLogoutError ? "unsaved" : "failed");
    setOpen(true);
  }, []);

  const requestLogout = useCallback(() => {
    logout().catch(report);
  }, [logout, report]);

  // After "Cerrar sesión igual" / "Reintentar": nothing left worth saving.
  const forceLogout = useCallback(() => {
    setOpen(false);
    logout({ discardUnsaved: true }).catch(report);
  }, [logout, report]);

  const who = friendSession?.unsaved ? ` para ${friendSession.username}` : "";
  const logoutDialog = (
    <ConfirmDialog
      open={open}
      onOpenChange={setOpen}
      title={problem === "failed" ? "No se pudo cerrar sesión" : `Hay cambios sin guardar${who}`}
      description={
        problem === "failed"
          ? "Revisa tu conexión e inténtalo de nuevo."
          : "Si cierras sesión ahora se perderán."
      }
      cancelText="Cancelar"
      confirmText={problem === "failed" ? "Reintentar" : "Cerrar sesión igual"}
      destructive={problem !== "failed"}
      onConfirm={forceLogout}
    />
  );

  return { requestLogout, logoutDialog };
}
